/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKDR Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Admin Agent Runner page — /admin/agents/run
 *
 * Calls SkillAgent (CLI-only today) via the V1 server-side proxy
 * at /api/admin/agents/{list,run}. The browser NEVER sees the
 * service secret; the proxy attaches x-internal-secret server-side.
 *
 * Renders the SkillAgent run envelope as RETRIEVAL + GROUNDING:
 *   - Run status pill (done / awaiting_approval / error)
 *   - Executed tools (with actionClass + decision per toolPolicy)
 *   - Retrieved KB cards (knowledge.search output.sources)
 *   - Memory records the agent grounded on
 *   - Collapsible raw envelope for debugging
 *
 * Degrades gracefully pre-deploy:
 *   - If GET /agents/list fails, agent picker falls back to ["wms/wms-ops"].
 *   - "Try mock data" button renders the contract-shaped fixture without
 *     making any network call — so the UI is reviewable before SkillAgent
 *     ships an HTTP endpoint.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  MOCK_AGENTS,
  MOCK_RUN_ENVELOPE,
} from './AgentRunner.fixtures';

const AVAILABLE_TOOLS = [
  { id: 'knowledge.search', label: 'knowledge.search (RAG)' },
  { id: 'scout.discover',   label: 'scout.discover (web)' },
  { id: 'reasoning.run',    label: 'reasoning.run (persona reasoning)' },
];

const FALLBACK_AGENTS = [
  { id: 'wms/wms-ops', displayName: 'WMS Ops (fallback)' },
];

// Workflows the reasoning.run provider can dispatch. Each workflow has a
// different required input key — operational-consultation wants the user's
// question as `customer_question`, the stakeholder-debate workflow wants
// `decision_question`. Mapping table keeps the UI honest.
const REASONING_WORKFLOWS = [
  { id: 'wms_operational_consultation', label: 'wms_operational_consultation (single persona)', inputKey: 'customer_question' },
  { id: 'wms_stakeholder_debate',       label: 'wms_stakeholder_debate (multi-persona)',        inputKey: 'decision_question' },
];

const REASONING_LONG_TIMEOUT_MS = 240000;
const REASONING_DEFAULT_TIMEOUT_MS = 60000;

function statusPillStyle(runStatus) {
  if (runStatus === 'done') {
    return { dot: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', label: 'Done' };
  }
  if (runStatus === 'awaiting_approval') {
    return { dot: '#f59e0b', bg: '#fffbeb', border: '#fde68a', label: 'Awaiting approval' };
  }
  if (runStatus === 'error' || runStatus === 'failed') {
    return { dot: '#ef4444', bg: '#fef2f2', border: '#fecaca', label: 'Error' };
  }
  return { dot: '#78909C', bg: '#F0FAFB', border: '#B2EBF2', label: runStatus || 'Unknown' };
}

// Real SkillAgent envelope wraps toolPolicy[].decision in an object
// { decision: 'allowed'|'denied', reasonCode, requiresApproval, ... } —
// older mock used a bare string. Read defensively so a shape mismatch
// degrades to a label instead of crashing the page (React #31).
function readDecision(entry) {
  const d = entry?.decision;
  if (d == null) return { verdict: 'unknown', reason: null };
  if (typeof d === 'string') return { verdict: d, reason: null };
  if (typeof d === 'object') {
    const verdict =
      typeof d.decision === 'string' ? d.decision
      : typeof d.verdict === 'string' ? d.verdict
      : 'unknown';
    const reason = typeof d.reasonCode === 'string' ? d.reasonCode : null;
    return { verdict, reason };
  }
  return { verdict: 'unknown', reason: null };
}

function readActionClass(entry) {
  if (typeof entry?.actionClass === 'string') return entry.actionClass;
  const nested = entry?.request?.policyContext?.actionClass;
  if (typeof nested === 'string') return nested;
  return 'unknown';
}

function decisionColor(verdict) {
  if (verdict === 'allow' || verdict === 'allowed') return '#00897B';
  if (verdict === 'deny' || verdict === 'denied') return '#991b1b';
  return '#546E7A';
}

const AgentRunner = () => {
  const navigate = useNavigate();

  const [agents, setAgents] = useState(FALLBACK_AGENTS);
  const [agentId, setAgentId] = useState(FALLBACK_AGENTS[0].id);
  const [goal, setGoal] = useState('');
  const [selectedTools, setSelectedTools] = useState(['knowledge.search']);
  const [reasoningWorkflowId, setReasoningWorkflowId] = useState(REASONING_WORKFLOWS[0].id);

  const [loadingAgents, setLoadingAgents] = useState(true);
  const [running, setRunning] = useState(false);
  const [envelope, setEnvelope] = useState(null);
  const [error, setError] = useState('');
  const [usingMock, setUsingMock] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingAgents(true);
        const res = await adminApi.get('/api/admin/agents/list');
        const list = Array.isArray(res.data?.agents) ? res.data.agents : [];
        if (cancelled) return;
        if (list.length > 0) {
          setAgents(list);
          setAgentId(list[0].id);
        }
      } catch (err) {
        // Pre-deploy: fall back to hardcoded agent; do not show as an error.
        console.warn('[AgentRunner] /agents/list unavailable — using fallback:', err.message);
      } finally {
        if (!cancelled) setLoadingAgents(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleTool = (toolId) => {
    setSelectedTools((prev) =>
      prev.includes(toolId) ? prev.filter((t) => t !== toolId) : [...prev, toolId]);
  };

  const isReasoningSelected = selectedTools.includes('reasoning.run');

  const handleRun = async () => {
    if (!goal.trim()) {
      setError('Enter a goal before running the agent.');
      return;
    }
    setRunning(true);
    setError('');
    setEnvelope(null);
    setUsingMock(false);

    // Build per-tool inputs. reasoning.run needs a workflow_id + the workflow's
    // required input key (varies per workflow). Use the dropdown selection to
    // pick the right key and pipe the goal text into it.
    const payload = {
      agentId,
      goal: goal.trim(),
      tools: selectedTools,
      inferenceOutputMode: 'json',
    };
    if (isReasoningSelected) {
      const wf = REASONING_WORKFLOWS.find((w) => w.id === reasoningWorkflowId)
        || REASONING_WORKFLOWS[0];
      payload.toolInputs = {
        'reasoning.run': {
          workflow_id: wf.id,
          inputs: { [wf.inputKey]: goal.trim() },
        },
      };
    }

    try {
      const res = await adminApi.post('/api/admin/agents/run', payload, {
        timeout: isReasoningSelected ? REASONING_LONG_TIMEOUT_MS : REASONING_DEFAULT_TIMEOUT_MS,
      });
      setEnvelope(res.data);
    } catch (err) {
      const code = err.response?.data?.code;
      const upstream = err.response?.data?.error || err.message;
      if (code === 'SKILLAGENT_UNCONFIGURED') {
        setError(
          'SkillAgent is not yet deployed (SKILLAGENT_BASE_URL unset on this backend). '
          + 'Use "Try mock data" below to preview the UI with a contract-shaped fixture.'
        );
      } else {
        setError(`Run failed: ${upstream}`);
      }
    } finally {
      setRunning(false);
    }
  };

  const handleMock = () => {
    setEnvelope(MOCK_RUN_ENVELOPE);
    setUsingMock(true);
    setError('');
    setAgents(MOCK_AGENTS);
    if (!MOCK_AGENTS.find((a) => a.id === agentId)) setAgentId(MOCK_AGENTS[0].id);
    if (!goal.trim()) setGoal(MOCK_RUN_ENVELOPE.goal);
  };

  // ─── Envelope readers (defensive — every field is optional) ───
  const summary = envelope?.summary || {};
  const runtime = envelope?.runtime || {};
  const toolPolicy = Array.isArray(runtime.toolPolicy) ? runtime.toolPolicy : [];
  const memoryRecords = runtime.memory?.read?.result?.records || [];
  const executedTools = Array.isArray(summary.executedTools) ? summary.executedTools : [];
  const failedTools = Array.isArray(summary.failedTools) ? summary.failedTools : [];

  const knowledgeOutputs = toolPolicy.filter(
    (t) => t.toolId === 'knowledge.search' && t.output && t.output.sources
  );

  // reasoning.run lands as a toolPolicy entry whose output carries the
  // synthesised practitioner-voice answer (final_text) + per-stage trace
  // (stage_outputs). Defensive: only consider entries where final_text is
  // a non-empty string — knowledge-only runs simply skip this panel.
  const reasoningOutputs = toolPolicy.filter(
    (t) => t.toolId === 'reasoning.run'
      && typeof t?.output?.final_text === 'string'
      && t.output.final_text.trim() !== ''
  );

  const pill = statusPillStyle(summary.runStatus);

  return (
    <AdminLayout showBackButton={true} backPath="/admin/dashboard">
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px 20px', fontFamily: 'Inter, system-ui, sans-serif' }}>
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#0D2233', margin: 0 }}>
            Run Agent
          </h1>
          <p style={{ color: '#546E7A', fontSize: '14px', marginTop: '6px' }}>
            Invoke a SkillAgent manifest and inspect what it retrieved + grounded on.
          </p>
        </div>

        {/* Honest framing */}
        <div style={{
          background: '#F0FAFB',
          border: '1px solid #B2EBF2',
          borderRadius: '8px',
          padding: '12px 14px',
          color: '#1A3A4A',
          fontSize: '13px',
          marginBottom: '20px',
          lineHeight: 1.5,
        }}>
          <strong>What this shows.</strong> This page renders the agent's <em>retrieval</em>
          {' '}+ <em>grounding</em>: tools executed, KB sources found, memory records loaded.
          A polished natural-language answer is a later addition.
        </div>

        {/* Inputs panel */}
        <div style={{
          background: '#ffffff',
          border: '1px solid #E0F7FA',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 4px 20px rgba(0, 188, 212, 0.10)',
          marginBottom: '20px',
        }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#1A3A4A', marginBottom: '6px' }}>
            Agent
          </label>
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            disabled={loadingAgents || running}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid #B2EBF2',
              borderRadius: '4px',
              background: '#fff',
              color: '#0D2233',
              marginBottom: '16px',
              fontFamily: 'inherit',
            }}
          >
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName ? `${a.displayName} (${a.id})` : a.id}
              </option>
            ))}
          </select>

          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#1A3A4A', marginBottom: '6px' }}>
            Goal
          </label>
          <textarea
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            disabled={running}
            rows={3}
            placeholder="e.g. Explain how velocity-class slotting reduces putaway time."
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid #B2EBF2',
              borderRadius: '4px',
              background: '#fff',
              color: '#0D2233',
              marginBottom: '16px',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />

          <div style={{ fontSize: '13px', fontWeight: 600, color: '#1A3A4A', marginBottom: '6px' }}>
            Tools
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {AVAILABLE_TOOLS.map((t) => (
              <label
                key={t.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  border: `1px solid ${selectedTools.includes(t.id) ? '#00BCD4' : '#B2EBF2'}`,
                  background: selectedTools.includes(t.id) ? '#E8F4F8' : '#fff',
                  borderRadius: '100px',
                  fontSize: '13px',
                  color: '#1A3A4A',
                  cursor: running ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedTools.includes(t.id)}
                  onChange={() => toggleTool(t.id)}
                  disabled={running}
                  style={{ margin: 0 }}
                />
                {t.label}
              </label>
            ))}
          </div>

          {/* Workflow picker — visible only when reasoning.run is selected, */}
          {/* because workflow_id is required input for that provider only.   */}
          {isReasoningSelected && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#1A3A4A', marginBottom: '6px' }}>
                Reasoning workflow
              </label>
              <select
                value={reasoningWorkflowId}
                onChange={(e) => setReasoningWorkflowId(e.target.value)}
                disabled={running}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #B2EBF2',
                  borderRadius: '4px',
                  background: '#fff',
                  color: '#0D2233',
                  fontFamily: 'inherit',
                }}
              >
                {REASONING_WORKFLOWS.map((w) => (
                  <option key={w.id} value={w.id}>{w.label}</option>
                ))}
              </select>
              <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#78909C' }}>
                Goal is passed as <code>{REASONING_WORKFLOWS.find((w) => w.id === reasoningWorkflowId)?.inputKey}</code>.
                Reasoning can take 1–3 minutes — be patient on first call (cold start).
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={handleRun}
              disabled={running || !goal.trim()}
              style={{
                background: '#00BCD4',
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: running || !goal.trim() ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 15px rgba(0, 188, 212, 0.30)',
                opacity: running || !goal.trim() ? 0.6 : 1,
                fontFamily: 'inherit',
              }}
            >
              {running ? 'Running…' : 'Run agent'}
            </button>

            <button
              onClick={handleMock}
              disabled={running}
              style={{
                background: '#fff',
                color: '#0097A7',
                border: '1px solid #B2EBF2',
                padding: '10px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: running ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
              title="Render the page with a contract-shaped fixture (no network call)"
            >
              Try mock data
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            padding: '12px 14px',
            borderRadius: '8px',
            fontSize: '13px',
            marginBottom: '20px',
          }}>
            {error}
          </div>
        )}

        {/* Running */}
        {running && (
          <LoadingSpinner
            message={isReasoningSelected
              ? 'Reasoning can take 1–3 minutes (cold start on first call)…'
              : 'Running agent…'}
          />
        )}

        {/* Result */}
        {envelope && !running && (
          <div data-testid="agent-result" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Status pill */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 14px',
                borderRadius: '100px',
                background: pill.bg,
                border: `1px solid ${pill.border}`,
                fontSize: '13px',
                fontWeight: 500,
                color: '#1A3A4A',
              }}>
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: pill.dot, display: 'inline-block',
                }} />
                {pill.label}
              </span>
              {usingMock && (
                <span style={{ fontSize: '12px', color: '#546E7A', fontStyle: 'italic' }}>
                  (showing mock fixture — SkillAgent not called)
                </span>
              )}
            </div>

            {/* Tools executed */}
            <div style={{
              background: '#fff',
              border: '1px solid #E0F7FA',
              borderRadius: '8px',
              padding: '16px',
              boxShadow: '0 4px 20px rgba(0, 188, 212, 0.10)',
            }}>
              <h3 style={{ margin: 0, marginBottom: '10px', fontSize: '15px', fontWeight: 600, color: '#0D2233' }}>
                Tools executed
              </h3>
              {toolPolicy.length === 0 ? (
                <p style={{ color: '#78909C', fontSize: '13px', margin: 0 }}>No tools invoked.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {toolPolicy.map((t, i) => {
                    const toolId = typeof t?.toolId === 'string' ? t.toolId : `tool[${i}]`;
                    const actionClass = readActionClass(t);
                    const { verdict, reason } = readDecision(t);
                    return (
                      <li key={`${toolId}-${i}`} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 0',
                        borderBottom: i < toolPolicy.length - 1 ? '1px solid #F0FAFB' : 'none',
                        fontSize: '13px',
                      }}>
                        <code style={{ background: '#F0FAFB', padding: '2px 8px', borderRadius: '4px', color: '#0D2233' }}>
                          {toolId}
                        </code>
                        <span style={{
                          background: '#E8F4F8',
                          color: '#0097A7',
                          padding: '2px 8px',
                          borderRadius: '100px',
                          fontSize: '11px',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                        }}>
                          {actionClass}
                        </span>
                        <span style={{
                          color: decisionColor(verdict),
                          fontSize: '12px',
                          fontWeight: 500,
                        }}>
                          {verdict}
                          {reason ? (
                            <span style={{ color: '#78909C', marginLeft: '6px', fontWeight: 400 }}>
                              ({reason})
                            </span>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#546E7A' }}>
                executedTools: <code>{JSON.stringify(executedTools)}</code>
                {failedTools.length > 0 && (
                  <> · failedTools: <code style={{ color: '#991b1b' }}>{JSON.stringify(failedTools)}</code></>
                )}
              </div>
            </div>

            {/* Retrieved (knowledge.search sources) */}
            {knowledgeOutputs.length > 0 && (
              <div style={{
                background: '#fff',
                border: '1px solid #E0F7FA',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: '0 4px 20px rgba(0, 188, 212, 0.10)',
              }}>
                <h3 style={{ margin: 0, marginBottom: '10px', fontSize: '15px', fontWeight: 600, color: '#0D2233' }}>
                  Retrieved (knowledge.search)
                </h3>
                {knowledgeOutputs.map((t, ti) => {
                  const sources = Array.isArray(t.output?.sources) ? t.output.sources : [];
                  const context = typeof t?.output?.context === 'string' ? t.output.context : null;
                  return (
                    <div key={`ko-${ti}`} style={{ marginBottom: ti < knowledgeOutputs.length - 1 ? '14px' : 0 }}>
                      {context && (
                        <p style={{
                          background: '#F0FAFB',
                          border: '1px solid #B2EBF2',
                          borderRadius: '4px',
                          padding: '10px 12px',
                          fontSize: '13px',
                          color: '#1A3A4A',
                          lineHeight: 1.5,
                          margin: '0 0 12px 0',
                        }}>
                          {context}
                        </p>
                      )}
                      {sources.length === 0 ? (
                        <p style={{ color: '#78909C', fontSize: '13px', margin: 0 }}>No sources returned.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {sources.map((s, i) => {
                            const title = typeof s?.title === 'string' ? s.title
                              : typeof s?.docId === 'string' ? s.docId
                              : `Source ${i + 1}`;
                            const snippet = typeof s?.snippet === 'string' ? s.snippet : null;
                            return (
                              <div key={`src-${ti}-${i}`} style={{
                                border: '1px solid #E0F7FA',
                                borderRadius: '4px',
                                padding: '10px 12px',
                                background: '#fff',
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                                  <strong style={{ fontSize: '13px', color: '#0D2233' }}>
                                    {title}
                                  </strong>
                                  {typeof s?.similarity === 'number' && (
                                    <span style={{ fontSize: '11px', color: '#0097A7', fontFamily: 'monospace' }}>
                                      sim {s.similarity.toFixed(3)}
                                    </span>
                                  )}
                                </div>
                                {snippet && (
                                  <p style={{ margin: 0, fontSize: '12px', color: '#546E7A', lineHeight: 1.5 }}>
                                    {snippet}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Memory loaded */}
            <div style={{
              background: '#fff',
              border: '1px solid #E0F7FA',
              borderRadius: '8px',
              padding: '16px',
              boxShadow: '0 4px 20px rgba(0, 188, 212, 0.10)',
            }}>
              <h3 style={{ margin: 0, marginBottom: '10px', fontSize: '15px', fontWeight: 600, color: '#0D2233' }}>
                Memory loaded
              </h3>
              {memoryRecords.length === 0 ? (
                <p style={{ color: '#78909C', fontSize: '13px', margin: 0 }}>No memory records.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {memoryRecords.map((r, i) => {
                    const key = typeof r?.key === 'string' ? r.key : null;
                    const body = typeof r?.content === 'string' ? r.content
                      : typeof r?.value === 'string' ? r.value
                      : JSON.stringify(r);
                    const source = typeof r?.source === 'string' ? r.source : null;
                    return (
                      <div key={`mem-${i}`} style={{
                        border: '1px solid #E0F7FA',
                        borderRadius: '4px',
                        padding: '10px 12px',
                        background: '#F0FAFB',
                      }}>
                        {key && (
                          <code style={{ fontSize: '11px', color: '#0097A7', display: 'block', marginBottom: '4px' }}>
                            {key}
                          </code>
                        )}
                        <p style={{ margin: 0, fontSize: '13px', color: '#1A3A4A', lineHeight: 1.5 }}>
                          {body}
                        </p>
                        {source && (
                          <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#78909C' }}>
                            source: {source}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Reasoned Answer — only renders when reasoning.run produced final_text */}
            {reasoningOutputs.map((entry, ri) => {
              const out = entry.output || {};
              const finalText = typeof out.final_text === 'string' ? out.final_text : '';
              const paragraphs = finalText.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
              const workflowId = typeof out.workflow_id === 'string' ? out.workflow_id : null;
              const runId = typeof out.run_id === 'string' ? out.run_id : null;
              const state = typeof out.state === 'string' ? out.state : null;
              const stageOutputs = Array.isArray(out.stage_outputs) ? out.stage_outputs : [];
              return (
                <div
                  key={`reasoning-${ri}`}
                  data-testid="reasoned-answer"
                  style={{
                    background: '#fff',
                    border: '1px solid #B2EBF2',
                    borderRadius: '8px',
                    padding: '20px',
                    boxShadow: '0 4px 20px rgba(0, 188, 212, 0.10)',
                  }}
                >
                  <h3 style={{
                    margin: 0,
                    marginBottom: '6px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#00838F',
                  }}>
                    Reasoned answer
                  </h3>
                  {(workflowId || state || runId) && (
                    <div style={{ fontSize: '11px', color: '#78909C', marginBottom: '14px' }}>
                      {workflowId && <span>workflow: <code>{workflowId}</code></span>}
                      {state && <span> · state: <code>{state}</code></span>}
                      {runId && <span> · run: <code>{runId}</code></span>}
                    </div>
                  )}
                  <div style={{ fontSize: '14px', color: '#0D2233', lineHeight: 1.6 }}>
                    {paragraphs.length === 0 ? (
                      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{finalText}</p>
                    ) : (
                      paragraphs.map((p, pi) => (
                        <p key={`rp-${ri}-${pi}`} style={{ margin: pi === 0 ? '0 0 12px 0' : '0 0 12px 0', whiteSpace: 'pre-wrap' }}>
                          {p}
                        </p>
                      ))
                    )}
                  </div>
                  {stageOutputs.length > 0 && (
                    <details style={{ marginTop: '14px' }}>
                      <summary style={{ cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#0097A7' }}>
                        Reasoning stages ({stageOutputs.length})
                      </summary>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                        {stageOutputs.map((s, si) => {
                          const stageId = typeof s?.stage_id === 'string' ? s.stage_id : `stage[${si}]`;
                          const archetype = typeof s?.archetype === 'string' ? s.archetype : null;
                          const tier = typeof s?.fallback_tier === 'string' ? s.fallback_tier : null;
                          const llmText = typeof s?.llm_text === 'string' ? s.llm_text : '';
                          return (
                            <div key={`stage-${ri}-${si}`} style={{
                              border: '1px solid #E0F7FA',
                              borderRadius: '4px',
                              padding: '10px 12px',
                              background: '#F0FAFB',
                            }}>
                              <div style={{ fontSize: '12px', color: '#0097A7', marginBottom: '6px' }}>
                                <code>{stageId}</code>
                                {archetype && <span style={{ color: '#546E7A' }}> · {archetype}</span>}
                                {tier && <span style={{ color: '#546E7A' }}> · tier: {tier}</span>}
                              </div>
                              <p style={{ margin: 0, fontSize: '12px', color: '#1A3A4A', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                                {llmText}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}

            {/* Raw envelope (collapsible) */}
            <details style={{
              background: '#fff',
              border: '1px solid #E0F7FA',
              borderRadius: '8px',
              padding: '12px 16px',
              boxShadow: '0 4px 20px rgba(0, 188, 212, 0.10)',
            }}>
              <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#0D2233' }}>
                Raw envelope (debug)
              </summary>
              <pre style={{
                marginTop: '10px',
                padding: '12px',
                background: '#0D2233',
                color: '#A7F3D0',
                borderRadius: '4px',
                fontSize: '11px',
                overflowX: 'auto',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                maxHeight: '400px',
              }}>
                {JSON.stringify(envelope, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AgentRunner;
